import dayjs from 'dayjs/esm'
import type { ConfigType } from 'dayjs/esm'
import { asEndOfDay, DayjsRange } from '~/lib/gantt'

export const DayjsWorkdays = [1, 2, 3, 4, 5]
export const DayjsSaturdays = [6]
export const DayjsSundays = [0]

export enum DateFormatsEnum {
  PYTHON_BACKEND = 'YYYY-MM-DDTHH:mm:ss',
  ISO_8601 = 'YYYY-MM-DD',
  ISO_8601_MONTH = 'YYYY-MM',
  ISO_8601_TIME = 'YYYY-MM-DDTHH:mm:ssZ',
  DATE_TIME = 'DD.MM.YYYY HH:mm:ss',
  DATE_TIME_SHORT = 'DD.MM HH:mm',
  TIME = 'HH:mm:ss',
  TIME_SHORT = 'HH:mm',
  EUROPE = 'DD.MM.YY',
  EUROPE_LONG = 'DD.MM.YYYY',
  DAY_MONTH_HUMAN = 'DD. MMM',
  DATE_HUMAN = 'DD. MMMM YYYY',
}

/**
 * Used for z.string().date() or z.string().datetime()
 * See: https://v3.zod.dev/?id=dates
 */
export const ZOD_STRING_DATE_FORMAT = DateFormatsEnum.ISO_8601
export const ZOD_STRING_DATE_TIME_FORMAT = DateFormatsEnum.ISO_8601_TIME

/**
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 *
 * Example: '25.01.2025, 23:02:48 MEZ' '25.08.2025, 23:02:48 MESZ'
 */
const DefaultDateFormat = Intl.DateTimeFormat('de', { dateStyle: 'medium', timeStyle: 'long' })

/**
 * Native version of formatDate
 */
export function formatDateNative(date: string | Date | null | undefined, format = DefaultDateFormat) {
  if (!date) {
    return ''
  }
  if (typeof date === 'string') {
    date = new Date(date)
  }
  if (!isValidDate(date)) {
    return ''
  }
  return format.format(date)
}

function isValidDate(date: Date): boolean {
  try {
    return !isNaN(date.getTime())
  } catch (error) {
    return false
  }
}

/**
 * This function gets called a lot (specially when moving bars)
 * Should be as performant as possible
 *
 * to display a date in the European date format
 */
export function formatDate(
  date: Date | string | dayjs.Dayjs | null | undefined | unknown,
  format: DateFormatsEnum = DateFormatsEnum.EUROPE,
  options = {
    trimmed: true,
  },
): string {
  if (!date) {
    return options.trimmed ? '' : String(date)
  }
  const dayjsDate = dayjs(date as any) // dayjs actually handles everything
  if (!dayjsDate.isValid()) {
    return String(date)
  }
  return dayjsDate.format(format)
}

const year = new Date().getFullYear()
const start = new Date(year, 0, 1, 0, 0, 0)
export function formatHoursToDate(hours: number) {
  const target = new Date(start.getTime() + hours * 60 * 60 * 1000)
  return formatDate(target, DateFormatsEnum.DATE_TIME_SHORT) + ' Uhr'
}

/**
 * Convert a one-based SINA load-time index (LZ/NNF) to its calendar hour.
 * UTC arithmetic intentionally avoids daylight-saving shifts: like the backend,
 * an analysis year always advances in uninterrupted one-hour steps.
 */
export function lzToDate(lz: number, year: number): Date {
  const maxLz = hoursPerYear(year)
  if (!Number.isInteger(lz) || lz < 1 || lz > maxLz) {
    throw new RangeError(`LZ must be an integer between 1 and ${maxLz} for year ${year}`)
  }
  return new Date(Date.UTC(year, 0, 1) + (lz - 1) * 60 * 60 * 1000)
}

/** Gregorian leap-year check. */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

/** Number of one-hour LZ values in an analysis year. */
export function hoursPerYear(year: number): number {
  return isLeapYear(year) ? 8784 : 8760
}

/** Format a one-based SINA load-time index as German calendar date and hour. */
export function formatLzDate(lz: number, year: number): string {
  const date = lzToDate(lz, year)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}. ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
}

/**
 * Returns date in ISO 8601 format YYYY-MM-DD, YYYY-MM or null as prop for v-date-picker
 *
 * See: https://v2.vuetifyjs.com/en/api/v-date-picker/#props-value
 */
export function vuetifyDatePickerDate(
  date: ConfigType,
  format: DateFormatsEnum.ISO_8601 | DateFormatsEnum.ISO_8601_MONTH = DateFormatsEnum.ISO_8601,
) {
  const dateReturn = dayjs(date)
  if (dateReturn.isValid()) {
    return dateReturn.format(format)
  }
  return null
}

export function sortDate(a: string, b: string) {
  return new Date(a).getTime() - new Date(b).getTime()
}

export const sortByStart = sortDateByKey<{ start: string }>('start')
export function sortDateByKey<T extends object, K extends keyof T = keyof T>(key: K) {
  return (a: T, b: T) => dayjs(a[key] as string | number | Date).diff(b[key] as string | number | Date)
}

/**
 * Calculate the start and end dates from a list of date-strings
 */
export function calcStartEndRanges(dates: string[]): DayjsRange[] {
  if (dates.length === 0) {
    return []
  }
  // convert dates array into array of objects containing time range with "start" and "end" date
  const startEndRanges: DayjsRange[] = []

  // first date in array is always start
  let start = dates[0]
  for (const date of dates) {
    const index = dates.indexOf(date)
    const currentDate = dayjs(date)
    // on last entry
    if (dates.length === index + 1) {
      startEndRanges.push({
        start: dayjs(start),
        end: asEndOfDay(currentDate),
      })
      continue
    }

    const followingDate = dayjs(dates[index + 1])
    // check if succeeding date is continuous
    if (currentDate.add(1, 'day').isSame(followingDate)) {
      continue
    }
    // if not continuous, add range
    startEndRanges.push({ start: dayjs(start), end: asEndOfDay(currentDate) })

    // init new range start
    start = dates[index + 1]
  }
  return startEndRanges
}

/**
 * Backend returns dates without any TZ information and uses UTC
 *
 * UTC datetime strings either end with "+00:00" or "Z"
 */
export function addMissingTimezoneUTC<T extends string | null | undefined>(date: T): string | T {
  if (typeof date !== 'string' || !date.length) {
    return date
  }
  if (date.endsWith('Z')) {
    return date as string
  }
  if (date.endsWith('+00:00')) {
    return date.replace('+00:00', 'Z')
  }
  // Timezone already provided
  // E.G., ends with "-08:11" or "+03:00"
  if (/[+-]\d{2}:\d{2}$/.test(date)) {
    return date
  }
  return date + 'Z'
}
